import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ActionText from '../../core/ActionText';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import PagingFilter from '../../core/PagingFilter';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { globalStyles } from '../../core/style/gstheme';
import { makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import ConceptData from '../taxonomies/ConceptData';
import ConceptStructure from '../taxonomies/ConceptStructure';

const styles = Object.assign({}, globalStyles);

class FloatingConceptView extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      initialSearch: true,
      concepts: [],
      text: "",
      page: 1,
      noneFound: false,
      noResults: true,
      pageCount: null,
      isErrored: null
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.handlePageChange(1, true);
  }

  handlePageRefresh = () => {
    this.setState({ text: "", initialSearch: true }, () => this.handlePageChange(this.state.page));
  }

  handleFilterChange = (text) => {
    this.setState({ text, initialSearch: text === "" }, () => this.handlePageChange(this.state.page, text === ""));
  }

  handlePageChange = async (page = 1, initialSearch = false) => {
    if (!initialSearch && this.state.text.length < 1) return;
    this.setState(
      { isLoading: true },
      () => {
        let search = [{
          predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
          value: "http://www.w3.org/2004/02/skos/core#Concept"
        }];
        if (this.state.text !== "") {
          search.push({
            predicate: "http://www.w3.org/2004/02/skos/core#prefLabel",
            value: this.state.text,
            isExactMatch: false
          });
        }
        else {
          search.push({
            predicate: "http://www.w3.org/2004/02/skos/core#prefLabel"
          });
        }
        this.doSearch({
          search: search,
          noCount: true,
          retainOrder: initialSearch,
          ...(initialSearch) && { fast: true },
          excludeByPath: [
            {
              predicate: [
                "http://www.w3.org/2004/02/skos/core#inScheme"
              ]
            }
          ],
          excludeByPathOnly: true,
          graph: [this.props.graph],
          entailment: this.props.skosXL ? ["propertyChain"] : [],
          predicates: ["http://www.w3.org/2004/02/skos/core#prefLabel"],
          page: page,
          language: this.props.viewLang,
          pageSize: 50,
          sort: ["http://www.w3.org/2004/02/skos/core#prefLabel"]
        })
          .promise
          .then(data => {
            if (data.results && data.results.length > 0) {
              const sortedConcepts = this.sortConcepts(data.results);
              const conceptIDs = sortedConcepts.map(result => result.id);
              this.loadData(conceptIDs, {})
                .promise
                .then(() => this.setState({
                  concepts: conceptIDs,
                  page,
                  noResults: false,
                  noneFound: false,
                  isLoading: false
                }))
                .catch((e) => {
                  if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                });
            }
            else {
              this.setState({
                isLoading: false,
                page: 1,
                noneFound: true,
                noResults: false
              });
            }
          })
          .catch((e) => {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
          });
      });
  }

  static getDerivedStateFromProps(props, state) {
    if (!state.isLoading && props.updateAt !== state.refreshAt) {
      return {
        concepts: null,
        noResults: true,
        initialSearch: true,
        refreshAt: props.updateAt
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isLoading && this.state.concepts === null && this.state.noResults === true) {
      this.handlePageChange(this.state.page, this.state.initialSearch);
    }
  }

  doSearch(payload) {
    this.cancellable = makeCancelable(
      GS.search(payload)
    );
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(
      GS.loadData(iris, options, this.props.graph, true)
    );
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  sortConcepts(concepts) {
    return concepts.sort(function (a, b) {
      let orderA = a.prefLabel ? a.prefLabel[Object.keys(a.prefLabel)[0]].toLowerCase() : "";
      let orderB = b.prefLabel ? b.prefLabel[Object.keys(b.prefLabel)[0]].toLowerCase() : "";
      if (orderA < orderB) return -1;
      if (orderA > orderB) return 1;
      return 0;
    });
  }

  handleChange = name => event => {
    this.setState({ [name]: event.target.checked }, () => this.handlePageChange(this.state.page, this.state.text === ""));
  }

  render() {

    const { isLoading, concepts, text, isErrored, noneFound } = this.state;
    const { activeConcept, onSelect, defaultLang, viewLang, onSelectViewLanguage, showLabelDetail,
      onDeleteConcept, classes, appBar, isReadOnly, graph, project, entitlements, showFloatingLabel } = this.props;

    if (isErrored) return <Errored data={isErrored} />;

    return (
      <ProjectConsumer>
        {({ projectLang }) => (
          <div>
            <div className={classes.controlPanel}>
              <div className={classes.views}>
                {appBar}
              </div>
              <div className={classes.controlPanelInner}>
                <div className={classes.controlPanelSearch} style={projectLang.length > 1 ? { height: '120px' } : { height: '60px' }}>
                  <PagingFilter
                    autoFocus={true}
                    handleFilterChange={this.handleFilterChange}
                    text={text}
                    maxLength={50}
                    placeholder="Max 50 displayed. Filter to refine." />
                </div>
              </div>
              <div className={classes.tree} style={{ top: '170px' }}>
                {
                  isLoading ?
                    <Loading />
                    :
                    <ConceptStructure
                      concepts={concepts}
                      graph={graph}
                      hideInScheme={true}
                      noneFound={noneFound}
                      activeConcept={activeConcept}
                      defaultLang={defaultLang}
                      viewLang={viewLang}
                      isReadOnly={isReadOnly}
                      onSelect={onSelect} />
                }
              </div>
            </div>
            <div className={classes.viewLanguagePanel}>
              <ProjectLanguageSelector selectedLang={viewLang} includeLabel={false} onSelect={onSelectViewLanguage} />
            </div>
            <div data-test="info-panel" className={classes.infoPanel}>
              {activeConcept === null ?
                <ActionText offset="180px">Select a concept to view the detail.</ActionText>
                :
                <ConceptData
                  key={activeConcept}
                  handleNotAvailable={true}
                  id={activeConcept}
                  entitlements={entitlements}
                  viewLang={viewLang}
                  graph={graph}
                  showFloatingLabel={showFloatingLabel}
                  project={project}
                  showLabelDetail={showLabelDetail}
                  conceptIRI={activeConcept}
                  onDeleteConcept={onDeleteConcept} />
              }
            </div>
          </div>
        )}
      </ProjectConsumer>
    );
  }

}

FloatingConceptView.propTypes = {
  classes: PropTypes.object,
  appBar: PropTypes.node.isRequired,
  graph: PropTypes.string.isRequired,
  entitlements: PropTypes.array.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  skosXL: PropTypes.bool.isRequired,
  project: PropTypes.string.isRequired,
  onSelectViewLanguage: PropTypes.func.isRequired,
  updateAt: PropTypes.number,
  activeConcept: PropTypes.string,
  showFloatingLabel: PropTypes.func.isRequired,
  showLabelDetail: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  onDeleteConcept: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  deletedConcepts: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default withStyles(styles)(FloatingConceptView);